import type { ComponentType } from 'react';
import { CalendarDays, KeyRound, Newspaper, ShieldAlert, Wallet, Wrench, Zap, type LucideIcon } from 'lucide-react';
import type { HomeWidgetConfig } from '@config/env/types';
import { SupportWidget } from './SupportWidget';
import { EventsWidget } from './EventsWidget';
import { PanicWidget } from './PanicWidget';
import { UtilitiesWidget } from './UtilitiesWidget';
import { AccountWidget } from './AccountWidget';
import { NewsWidget } from './NewsWidget';
import { AccessManagementWidget } from './AccessManagementWidget';

export interface WidgetProps {
  config: HomeWidgetConfig;
}

interface WidgetDefinition {
  /** Default header title when the env config doesn't override it. */
  defaultTitle: string;
  /** Shown beside the widget on the Customize home page. */
  icon: LucideIcon;
  component: ComponentType<WidgetProps>;
}

/**
 * Registry of home dashboard widgets, keyed by `HomeWidgetConfig.type`. Add new
 * widgets here (e.g. a future `utilities` meter-usage widget) and reference them
 * from the env `home.widgets` JSON.
 */
export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {
  support: { defaultTitle: 'Helpdesk', icon: Wrench, component: SupportWidget },
  events: { defaultTitle: 'Upcoming events', icon: CalendarDays, component: EventsWidget },
  panic: { defaultTitle: 'Emergency', icon: ShieldAlert, component: PanicWidget },
  utilities: { defaultTitle: 'Utilities', icon: Zap, component: UtilitiesWidget },
  account: { defaultTitle: 'Account', icon: Wallet, component: AccountWidget },
  news: { defaultTitle: 'News', icon: Newspaper, component: NewsWidget },
  access: { defaultTitle: 'Visitors', icon: KeyRound, component: AccessManagementWidget },
};

export function widgetTitle(config: HomeWidgetConfig): string {
  return config.title ?? WIDGET_REGISTRY[config.type]?.defaultTitle ?? config.type;
}
